import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material';

export enum SnackbarType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

@Injectable({
  providedIn: 'root'
})
export class CustomSnackbarService {
  private defaultDuration = 5000; // 5 seconds

  constructor(private snackBar: MatSnackBar, private zone: NgZone) { }

  /**
   * Opens a basic snackbar with provided text and configuration
   * @param message Text to display in the snackbar
   * @param action Text for the action button (defaults to 'Close')
   * @param config Additional configuration options
   */
  openSnackBar(errorText: string, config: {}) {
    this.zone.run(() => {
      const snackBar = this.snackBar.open(errorText, 'Close', config);
      snackBar.onAction()
      .subscribe(_ => {
        snackBar.dismiss();
      });
    });
  }

  /**
   * Shows a success message in green
   * @param message Success message to display
   * @param duration Optional duration (defaults to 5000ms)
   */
  showSuccess(message: string, duration: number = this.defaultDuration): void {
    this.zone.run(() => {
      this.snackBar.open(message, 'Close', {
        duration: duration,
        panelClass: ['success-snackbar'],
        horizontalPosition: 'right',
        verticalPosition: 'top'
      });
    });
  }

  /**
   * Shows an error message in red
   * @param message Error message to display
   * @param duration Optional duration (defaults to 5000ms)
   */
  showError(message: string, duration: number = this.defaultDuration): void {
    this.zone.run(() => {
      this.snackBar.open(message, 'Close', {
        duration: duration,
        panelClass: ['error-snackbar'],
        horizontalPosition: 'right',
        verticalPosition: 'top'
      });
    });
  }

  /**
   * Shows a warning message in orange
   * @param message Warning message to display
   * @param duration Optional duration (defaults to 5000ms)
   */
  showWarning(message: string, duration: number = this.defaultDuration): void {
    this.zone.run(() => {
      this.snackBar.open(message, 'Close', {
        duration: duration,
        panelClass: ['warning-snackbar'],
        horizontalPosition: 'right',
        verticalPosition: 'top'
      });
    });
  }

  /**
   * Shows an info message in blue
   * @param message Info message to display
   * @param duration Optional duration (defaults to 5000ms)
   */
  showInfo(message: string, duration: number = this.defaultDuration): void {
    this.zone.run(() => {
      this.snackBar.open(message, 'Close', {
        duration: duration,
        panelClass: ['info-snackbar'],
        horizontalPosition: 'right',
        verticalPosition: 'top'
      });
    });
  }

  /**
   * Shows a custom snackbar with an icon
   * @param message Message to display
   * @param type Type of snackbar (success, error, warning, info)
   * @param icon Material icon name (optional)
   * @param duration Optional duration (defaults to 5000ms)
   */
  showWithIcon(message: string, type: SnackbarType, icon?: string, duration: number = this.defaultDuration): void {
    const iconMap = {
      [SnackbarType.SUCCESS]: 'check_circle',
      [SnackbarType.ERROR]: 'error',
      [SnackbarType.WARNING]: 'warning',
      [SnackbarType.INFO]: 'info'
    };
    
    const snackbarIcon = icon || iconMap[type];
    const panelClass = [`${type}-snackbar`, 'with-icon'];
    this.zone.run(() => {
      this.snackBar.open(`${message}`, 'Close', {
        duration: duration,
        panelClass: panelClass,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        data: { icon: snackbarIcon }
      });
    });
  }

  /**
   * Shows a persistent snackbar that doesn't auto-dismiss
   * @param message Message to display
   * @param type Type of snackbar (success, error, warning, info)
   */
  showPersistent(message: string, type: SnackbarType = SnackbarType.INFO): void {
    this.zone.run(() => {
      this.snackBar.open(message, 'Dismiss', {
        panelClass: [`${type}-snackbar`],
        horizontalPosition: 'right',
        verticalPosition: 'top'
      });
    });
  }
}